import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { DollarSign, FileText, Users, TrendingUp, Clock, CheckCircle, Pin, AlertTriangle, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getCurrencySymbol, formatCurrency } from '../utils/currency';
import { hasFeature } from '../utils/featureFlags';
import OnboardingTour from '../components/OnboardingTour';

const Dashboard = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [projects, setProjects] = useState([]);
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinnedProjects, setPinnedProjects] = useState([]);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [analyticsRes, projectsRes, incomeRes] = await Promise.all([
        apiClient.get('/income/analytics'),
        apiClient.get('/projects'),
        apiClient.get('/income')
      ]);
      
      const projectsData = Array.isArray(projectsRes.data) ? projectsRes.data : projectsRes.data.items || [];
      const incomeData = Array.isArray(incomeRes.data) ? incomeRes.data : incomeRes.data.items || [];

      setAnalytics(analyticsRes.data);
      setProjects(projectsData);
      setIncomeRecords(incomeData);

      // Load pinned projects from localStorage
      const pinnedIds = JSON.parse(localStorage.getItem('pinnedProjects') || '[]');
      const pinned = projectsData.filter(p => pinnedIds.includes(String(p.project_id)));
      setPinnedProjects(pinned);
    } catch (error) {
      // Don't show error toast if it's a 401 (interceptor handles it)
      if (error.response?.status !== 401) {
        toast.error('Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  const getProjectStatusData = () => {
    const statusCount = {
      'acquired': 0,
      'ongoing': 0,
      'completed': 0
    };
    
    projects.forEach(project => {
      if (statusCount.hasOwnProperty(project.status)) {
        statusCount[project.status]++;
      }
    });
    
    return [
      { name: 'Acquired', value: statusCount.acquired, color: '#94a3b8' },
      { name: 'Ongoing', value: statusCount['ongoing'], color: '#3b82f6' },
      { name: 'Completed', value: statusCount.completed, color: '#10b981' }
    ];
  };

  const getMonthlyIncomeData = () => {
    const monthlyData = {};
    
    incomeRecords.forEach(record => {
      if (record.payment_status === 'received' && record.received_date) {
        const month = new Date(record.received_date).toLocaleDateString('en-US', { month: 'short' });
        monthlyData[month] = (monthlyData[month] || 0) + record.amount;
      }
    });
    
    return Object.entries(monthlyData).map(([month, amount]) => ({
      month,
      income: amount
    }));
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await apiClient.post('/auth/resend-verification');
      toast.success('Verification email resent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  const getRecentProjects = () => {
    return projects
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);
  };

  const getDelayedProjects = () => {
    return projects.filter(project =>
      project.status === 'ongoing' &&
      project.end_date &&
      new Date(project.end_date) < new Date()
    );
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-in fade-in duration-500">
        <div className="space-y-2">
          <Skeleton className="h-10 w-[250px]" />
          <Skeleton className="h-4 w-[350px]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border-slate-100 shadow-sm">
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-3 w-40" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-[400px] w-full rounded-xl" />
          <Skeleton className="h-[400px] w-full rounded-xl" />
        </div>
      </div>
    );
  }

  const projectStatusData = getProjectStatusData();
  const monthlyIncomeData = getMonthlyIncomeData();
  const recentProjects = getRecentProjects();
  const delayedProjects = getDelayedProjects();

  return (
    <div className="space-y-8" data-testid="dashboard">
      <OnboardingTour />

      {/* Verification Alert */}
      {user && !user.is_verified && (
        <div data-testid="verification-alert" className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-2 rounded-full">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-amber-900 text-sm">Verify your email address</h3>
              <p className="text-amber-700 text-xs mt-0.5">Please check your inbox for a verification link to unlock all features.</p>
            </div>
          </div>
          <button
            data-testid="resend-verification-button"
            onClick={handleResendVerification}
            disabled={resending}
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-xs font-bold transition-colors disabled:opacity-50"
          >
            {resending ? (
              <RefreshCcw className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCcw className="h-3 w-3" />
            )}
            Resend Email
          </button>
        </div>
      )}

      <div>
        <h1 className="text-4xl font-bold text-slate-900">Welcome back, {user?.name}!</h1>
        <p className="text-slate-600 mt-2">Here's what's happening with your projects</p>
      </div>

      {/* Delayed Projects Section */}
      {delayedProjects.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-rose-100 p-2 rounded-full">
              <AlertTriangle className="h-5 w-5 text-rose-600" />
            </div>
            <div>
              <h3 className="font-semibold text-rose-900">Delayed Projects Attention Required</h3>
              <p className="text-rose-700 text-xs">The following projects are past their due date and still ongoing.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {delayedProjects.map(project => (
              <div
                key={project.project_id}
                className="bg-white p-3 rounded-lg border border-rose-100 shadow-sm cursor-pointer hover:shadow-md transition-all"
                onClick={() => window.location.href = `/projects/${project.project_id}`}
              >
                <div className="flex justify-between items-start">
                  <h4 className="font-bold text-slate-900 text-sm truncate">{project.title}</h4>
                  <span className="text-[10px] font-bold text-rose-600 uppercase bg-rose-50 px-1.5 py-0.5 rounded">Delayed</span>
                </div>
                <p className="text-[10px] text-slate-500 mt-1 flex items-center">
                  <Clock className="h-3 w-3 mr-1" />
                  Due: {new Date(project.end_date).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pinned Projects Section (Premium only) */}
      {hasFeature(user, 'Pinning') && pinnedProjects.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-indigo-600" />
            <h2 className="text-2xl font-bold text-slate-900">Pinned Projects</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pinnedProjects.map(project => (
              <Card key={project.project_id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = `/projects/${project.project_id}`}>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-bold text-lg text-slate-900">{project.title}</h3>
                    <Pin className="h-4 w-4 text-amber-500 fill-current" />
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{project.status}</span>
                      <span className="font-bold text-indigo-600">{project.progress_percentage}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                      <div className="bg-indigo-600 h-1.5 rounded-full" style={{ width: `${project.progress_percentage}%` }}></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-emerald-400 to-teal-600 text-white border-0 shadow-lg" data-testid="total-income-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Income</CardTitle>
            <DollarSign className="h-5 w-5" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{formatCurrency(analytics?.total_income || 0, user?.currency)}</div>
            <p className="text-xs mt-1 text-emerald-100">From completed projects</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid="pending-payments-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Pending Payments</CardTitle>
            <Clock className="h-5 w-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{formatCurrency(analytics?.pending_payments || 0, user?.currency)}</div>
            <p className="text-xs text-slate-500 mt-1">Awaiting payment</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid="active-projects-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Active Projects</CardTitle>
            <FileText className="h-5 w-5 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{analytics?.active_projects || 0}</div>
            <p className="text-xs text-slate-500 mt-1">In progress</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid="total-clients-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Clients</CardTitle>
            <Users className="h-5 w-5 text-rose-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{analytics?.total_clients || 0}</div>
            <p className="text-xs text-slate-500 mt-1">Active relationships</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Status Chart */}
        <Card className="border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle>Project Status Overview</CardTitle>
          </CardHeader>
          <CardContent>
            {projectStatusData.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={projectStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {projectStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-slate-400">
                No project data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Monthly Income Chart */}
        <Card className="border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle>Monthly Income Trend</CardTitle>
          </CardHeader>
          <CardContent>
            {monthlyIncomeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyIncomeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${getCurrencySymbol(user?.currency)}${value}`, 'Income']} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="income" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    name={`Income (${getCurrencySymbol(user?.currency)})`}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-slate-400">
                No income data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Projects & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle>Recent Projects</CardTitle>
          </CardHeader>
          <CardContent>
            {recentProjects.length > 0 ? (
              <div className="space-y-3">
                {recentProjects.map(project => (
                  <div
                    key={project.project_id}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{project.title}</p>
                      <p className="text-sm text-slate-500">{project.status}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-900">
                        {formatCurrency(project.total_budget, user?.currency)}
                      </p>
                      <p className="text-xs text-slate-500">{project.progress_percentage}% done</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No projects yet</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <a
              href="/clients"
              className="block p-4 rounded-lg bg-slate-50 hover:bg-indigo-50 hover:border-indigo-200 border border-transparent transition-all"
              data-testid="quick-action-add-client"
            >
              <p className="font-medium text-slate-900">Add New Client</p>
              <p className="text-sm text-slate-500">Start managing a new client relationship</p>
            </a>
            <a
              href="/projects"
              className="block p-4 rounded-lg bg-slate-50 hover:bg-indigo-50 hover:border-indigo-200 border border-transparent transition-all"
              data-testid="quick-action-create-project"
            >
              <p className="font-medium text-slate-900">Create Project</p>
              <p className="text-sm text-slate-500">Set up a new project for your clients</p>
            </a>
            <a
              href="/income"
              className="block p-4 rounded-lg bg-slate-50 hover:bg-indigo-50 hover:border-indigo-200 border border-transparent transition-all"
            >
              <p className="font-medium text-slate-900">Track Income</p>
              <p className="text-sm text-slate-500">Add new income records and track payments</p>
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
